/**
 * Список брендов для телефона.
 *
 * Таблица из тринадцати колонок на экране шириной 375 px не работает: её
 * приходится возить вбок, и за раз видно полтора значения. Поэтому на телефоне
 * бренд — карточка: имя, тир и видимость в первой строке, приоритеты и
 * категории во второй. Всё остальное открывается по нажатию, в карточке бренда.
 *
 * Правка значений здесь намеренно недоступна: на ходу правят редко, а
 * промахнуться пальцем по чипу легко. Нажатие ведёт в карточку, где у каждого
 * поля есть подпись и подтверждение.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Brand, ChannelCode } from '@shared/types.ts'
import {
  IconChevronDown, IconChevronRight, IconComment, IconWarning, TierBadge, chipColor,
} from '../../components/index.js'
import { brandCell, channelCell, type Overlay } from '../../lib/draft-overlay.js'
import { tableTexts as t } from './texts.table.js'
import './BrandCards.css'

export interface BrandCardsProps {
  brands: readonly Brand[]
  channel: ChannelCode
  overlay: Overlay
  /** Подписи значений справочников: чипы показывают их, а не коды. */
  labelOf: (set: string, value: string | null) => string | null
  /** Цвет чипа из справочника — тот же, что в таблице на большом экране. */
  colorOf: (set: string, value: string | null) => string | null
}

export function BrandCards({ brands, channel, overlay, labelOf, colorOf }: BrandCardsProps) {
  return (
    <ul className="cards" role="list">
      {brands.map((brand) => (
        <li key={brand.id}>
          <BrandCardRow brand={brand} channel={channel} overlay={overlay} labelOf={labelOf} colorOf={colorOf} />
        </li>
      ))}
    </ul>
  )
}

function BrandCardRow({ brand, channel, overlay, labelOf, colorOf }: {
  brand: Brand
  channel: ChannelCode
  overlay: Overlay
  labelOf: (set: string, value: string | null) => string | null
  colorOf: (set: string, value: string | null) => string | null
}) {
  const name = brandCell<string>(overlay, brand, 'name').value
  const unclear = brandCell<boolean>(overlay, brand, 'categoriesUnclear').value
  const tier = channelCell<string>(overlay, brand, channel, 'tier')
  const visibility = channelCell<string>(overlay, brand, channel, 'visibility')
  const seo = channelCell<string>(overlay, brand, channel, 'seoPriority')
  const context = channelCell<string>(overlay, brand, channel, 'contextPriority')
  const hidden = visibility.value === 'no'

  const [open, setOpen] = useState(false)

  const classes = ['card-row']
  if (hidden) classes.push('card-row--hidden')

  return (
    <div className={classes.join(' ')}>
      <Link className="card-row__main" to={`/brands/${brand.slug}?ch=${channel}`}>
        <span className="card-row__head">
          <span className="card-row__name t-brand t-truncate">{name}</span>
          {unclear ? (
            <span className="card-row__flag" aria-label={t.row.unclear}><IconWarning size={16} /></span>
          ) : null}
          <TierBadge tier={typeof tier.value === 'string' ? tier.value : null} />
          <IconChevronRight size={16} />
        </span>

        <span className="card-row__meta">
          {hidden ? <span className="card-chip card-chip--off">{labelOf('visibility', 'no')}</span> : null}

          {/* Два одинаковых «TOP-1» рядом без подписи не различить: слева
              приоритет в поиске, справа — в контексте. */}
          {typeof seo.value === 'string' ? (
            <span className="card-row__pair">
              <span className="card-row__pair-label t-meta">{t.card.seo}</span>
              <span
                className="enum-chip"
                data-set="seo_priority"
                data-value={seo.value}
                data-color={chipColor(colorOf('seo_priority', seo.value))}
              >
                {labelOf('seo_priority', seo.value)}
              </span>
            </span>
          ) : null}

          {typeof context.value === 'string' ? (
            <span className="card-row__pair">
              <span className="card-row__pair-label t-meta">{t.card.context}</span>
              <span
                className="enum-chip"
                data-set="context_priority"
                data-value={context.value}
                data-color={chipColor(colorOf('context_priority', context.value))}
              >
                {labelOf('context_priority', context.value)}
              </span>
            </span>
          ) : null}

          {brand.commentsCount > 0 ? (
            <span className="card-row__comments t-meta">
              <IconComment size={16} />
              {brand.commentsCount}
            </span>
          ) : null}
        </span>
      </Link>

      {/* Категории разворачиваются прямо в списке: на телефоне ради них
          приходилось открывать карточку и возвращаться обратно. */}
      {brand.categories.length > 0 ? (
        <button
          type="button"
          className="card-row__cats"
          aria-expanded={open}
          onClick={() => setOpen((previous) => !previous)}
        >
          {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          <span className="t-meta">{t.card.categories(brand.categories.length)}</span>
        </button>
      ) : null}

      {open ? (
        <ul className="card-cats-list">
          {[...brand.categories]
            .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
            .map((category) => (
              <li key={category.id} className="card-cats-list__row">
                <span className="card-cats-list__name t-cat t-truncate">{category.name}</span>
                <TierBadge tier={category.channels[channel]?.tier ?? null} />
              </li>
            ))}
        </ul>
      ) : null}
    </div>
  )
}
